import { z } from 'zod';

import { CorrelationMatrixSchema, RhoSchema } from './correlation.schema';
import { SectorIdSchema } from './enums';
import { TickerSchema } from './primitives';
import { SecuritySchema } from './security.schema';

/**
 * Defensive row ceilings. MEMBERS_MAX is sized above the plan's >2 000-name
 * trigger for the same reason as ROWS_MAX (dashboard.schema.ts); movers and
 * diversifiers are product-fixed shortlists.
 */
export const MEMBERS_MAX = 2_500;
export const MOVERS_MAX = 50;
export const DIVERSIFIERS_MAX = 20;

export const SectorSchema = z.object({
  id: SectorIdSchema,
  name: z.string().min(1).max(100),
});
export type Sector = z.infer<typeof SectorSchema>;

export const SectorAggregateSchema = z.object({
  /** Sector code — same value as `Sector.id`, under its wire name. */
  sc: SectorIdSchema,
  name: z.string().min(1).max(100),
  mcap: z.number().nonnegative(),
  /**
   * Cap-weighted total return for the active window, or `null` when NO member
   * carried weight (every member missing `mcap` or `ret[window]`).
   *
   * Nullable by ADR-0015: `0` here is a real flat sector, and coalescing the
   * unknown case into it rendered `+0.0%` in a neutral-green tile —
   * indistinguishable from a sector that genuinely finished flat, and reachable
   * on live data via any backfill gap.
   */
  ret: z.number().nullable(),
  memberCount: z.number().int().nonnegative(),
  advancers: z.number().int().nonnegative(),
  decliners: z.number().int().nonnegative(),
});
export type SectorAggregate = z.infer<typeof SectorAggregateSchema>;

export const SectorDiversifierSchema = z.object({
  ticker: TickerSchema,
  /** Mean pairwise correlation against the sector — same [-1, 1] domain as rho. */
  meanRho: RhoSchema,
});
export type SectorDiversifier = z.infer<typeof SectorDiversifierSchema>;

/**
 * Sector screen payload (plan §6.6 GET /v1/sectors/:id):
 * KPIs, members, movers, 10×10 matrix, diversifiers.
 */
export const SectorDetailSchema = z.object({
  sector: SectorSchema,
  aggregate: SectorAggregateSchema,
  members: z.array(SecuritySchema).max(MEMBERS_MAX),
  movers: z.array(SecuritySchema).max(MOVERS_MAX),
  matrix: CorrelationMatrixSchema,
  diversifiers: z.array(SectorDiversifierSchema).max(DIVERSIFIERS_MAX),
});
export type SectorDetail = z.infer<typeof SectorDetailSchema>;
